/* eslint-disable import/no-cycle */
/** @todo Fix import cycle */

import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

// --------------------------------- User Authentication ---------------------------------

/**
 * Requests verification code for a user with the specified `credentials`.
 * @param {object} credentials
 * @param {func} setError
 * @param {func} setLoading
 * @returns {object|null} MFA payload (message, method, address), or `{ code: 'password_not_set' }` if
 *   the account hasn't been set up, or `null` on other errors.
 */
export const login = async (credentials, setError, setLoading) => {
  setLoading(true)

  try {
    const data = await axios.post('/login/', credentials)
    setLoading(false)
    return data
  } catch (err) {
    if (err.response?.data?.code === 'password_not_set') {
      setLoading(false)
      return { code: 'password_not_set' }
    }
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Resends the invitation email for a user who hasn't set their password yet.
 * @param {object} payload - { email }
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns {boolean} `true` on success, `false` on error
 */
export const resendInvitation = async (payload, setError, setLoading, setSuccess) => {
  setLoading(true)

  try {
    await axios.post('/resend-invitation/', payload)
    setSuccess('Setup link sent! Check your email to set up your account.')
    setLoading(false)
    return true
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return false
  }
}

/**
 * Re-requests verification code and logs in a user with the specified `credentials`.
 * @param {object} credentials - { email, password, mfaDevice?, emailId? }. `emailId` targets one of
 *   the addresses from the login response's `mfaAddresses` (RSCX-377); omit to re-send to the
 *   address the user signed in with.
 * @param {func} setError
 * @param {func} setLoading
 * @returns {object} with MFA properties (message, method, obfuscated address, emailAddresses)
 */
export const resendLoginCode = async (credentials, setError, setLoading) => {
  setLoading(true)

  try {
    const data = await axios.post('/login/resend/', credentials)
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Submits verification code and logs in a user with the specified `payload`.
 * @param {object} payload
 * @param {func} setError
 */
export const loginConfirm = async (payload, setError) => {
  try {
    return await axios.post('/login/confirm/', payload)
  } catch (err) {
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * Requests a password reset for the specified user
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export async function forgotPassword(payload, setError, setLoading, setSuccess) {
  setLoading(true)

  try {
    const { data } = await axios.post('/password_reset/', payload)
    setSuccess('Check your email to reset your password.')
    setLoading(false)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Confirms the user's new password and optionally sets first/last name
 * @param {object} payload - Should include password, confirmPassword, token, and optionally firstName/lastName
 * @param {func} setError
 */
export async function resetPassword(payload, setError) {
  try {
    // Only send firstName and lastName if they are provided
    const data = {
      password: payload.password,
      token: payload.token,
    }
    if (payload.firstName) {
      data.first_name = payload.firstName
    }
    if (payload.lastName) {
      data.last_name = payload.lastName
    }
    return await axios.post('/password_reset/confirm/', data)
  } catch (err) {
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * Validates the specified `token` for the reset password flow
 * @param {string} token
 * @param {func} showError
 */
export async function validateResetToken(token, showError) {
  try {
    await axios.post('/password_reset/validate_token/', { token })
    return true
  } catch (err) {
    // Django Rest Reset Password returns a weird error message when the token is invalid.
    // Override it with a more user-friendly one.
    let message = getErrorMessage(err)
    if (message.includes('The OTP password entered is not valid'))
      message =
        'Your link is invalid or expired. Please request a new one by resetting your password.'

    showError(message)
    return false
  }
}

/**
 * Refreshes the specified `refresh` token
 * @param {string} refresh
 */
export function refreshAccessToken(refresh) {
  return axios.post('/token/refresh/', { refresh })
}

/**
 *
 * If possible, revoke the user's session.
 * In all cases, remove the user's data, including auth tokens, by clearing the store. This triggers
 * a change in PrivateRoute and redirects the user.
 *
 * @param {string} refresh - The user's refresh token
 * @param {function} clearStore - Function from RootStore to clear out all store data
 */
export async function logout(refresh, clearStore = () => {}) {
  try {
    await axios.post('/logout/', { refresh })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('User session could not be revoked.')
  }

  clearStore()
}

// --------------------------------- User Information ---------------------------------

/**
 * Gets the list of users.
 */
export const getUsers = async ({ sorting, pagination, searchTerm, filters }) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(
      `/users/${params}&expand=entity_roles,entity_roles.entity,entity_roles.jobs`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get a user by the specified `id`.
 * @param {string} id
 */
export const getUser = async (id) => {
  try {
    let updatedUser = await axios.get(`/users/${id}/?expand=entity_roles.entity`)

    // If the user has no active entity role and is not an Xverse admin,
    // we should set the activeEnityRole to the first role.
    if (
      updatedUser.activeEntity === null &&
      updatedUser.entityRoles &&
      updatedUser.entityRoles.length > 0 &&
      !updatedUser.isXverseAdmin
    ) {
      // eslint-disable-next-line prefer-destructuring
      updatedUser.activeEntity =
        updatedUser.entityRoles[0].entity?.id || updatedUser.entityRoles[0].entity
      updatedUser = await updateUser({
        id: updatedUser.id,
        activeEntity: updatedUser.activeEntity,
      })
    }

    return updatedUser
  } catch (err) {
    // Preserve just the response status so callers can distinguish an explicit
    // auth rejection (401/403) from a network error or 5xx (see main.jsx
    // boot). Deliberately not the axios error itself — it carries request
    // headers (including Authorization), which must not end up in console
    // logs or error reporting.
    throw new Error(getErrorMessage(err), { cause: { status: err.response?.status ?? null } })
  }
}

/**
 * Add a new user.
 * @param {object} payload - The user data to be added.
 */
export const addUser = async (payload) => {
  try {
    return await axios.post('/users/', payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update an user.
 * @param {object} payload - The user data to be updated.
 */
export const updateUser = async (payload) => {
  try {
    return await axios.patch(`/users/${payload.id}/?expand=entity_roles.entity`, payload)
  } catch (err) {
    const msg = getErrorMessage(err)

    if (msg === 'The fields entity, role must make a unique set.') {
      throw new Error('This Account already has an Owner role assigned.')
    } else {
      throw new Error(msg)
    }
  }
}

/**
 * Update the specified the password of the authenticated user
 * @param {object} payload
 */
export const updateUserPassword = async (payload) => {
  try {
    return await axios.put('/update-password/', payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Admin sets a user's password (without requiring old password)
 * @param {object} payload - { userId, newPassword }
 */
export const adminSetUserPassword = async (payload) => {
  try {
    return await axios.post(`/users/${payload.userId}/admin/set-password/`, {
      new_password: payload.newPassword,
    })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Reinvite a user.
 * @param {string} id - The user id to be reinvited.
 */
export const reinviteUser = async (id) => {
  try {
    return await axios.patch(`/users/${id}/`, { reinviteUser: true })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Deletes an user.
 * @param {string} id - The user id to be deleted.
 */
export const deleteUser = async (id) => {
  try {
    return await axios.delete(`/users/${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

// --------------------------------- Change MFA Settings ---------------------------------

/**
 * Requests an email change with the specified `email` for the authenticated user
 * @param {string} email
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const requestEmailChange = async (
  email,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/change_email/`, { email })

    setLoading(false)
    setSuccess(data.message)
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Requests an phone change with the specified `phone` for the authenticated user
 * @param {string} phoneNumber
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const requestPhoneChange = async (
  phoneNumber,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/change_phone_number/`, { phoneNumber })

    setLoading(false)
    setSuccess(data.message ? data.message : 'Phone number removed.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Submits the specified verification `code`.
 * @param {string} code
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @param {func} setUser
 * @returns `data` results
 */
export const confirmEmailChange = async (
  code,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
  setUser = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/change_email/confirm/`, { code })

    setUser(data)
    setLoading(false)
    setSuccess('Email address updated.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Submits the specified verification `code`.
 * @param {string} code
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @param {func} setUser
 * @returns `data` results
 */
export const confirmPhoneChange = async (
  code,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
  setUser = () => {},
) => {
  setLoading(true)

  try {
    const data = await axios.post(`/change_phone_number/confirm/`, { code })

    setUser(data)
    setLoading(false)
    setSuccess('Phone number updated.')
    return data
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

// --------------------------------- Unused ---------------------------------

export function confirmAccountOrEmail(token, uid) {
  return axios.get(`/confirm/?token=${token}&uid=${uid}`)
}

// --------------------------------- Email Addresses (RSCX-351) ---------------------------------

/**
 * List a user's additional email addresses.
 * @param {string} userId
 */
export const getUserEmails = async (userId) => {
  try {
    return await axios.get(`/users/${userId}/emails/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Add an additional email address to a user.
 * @param {object} payload - { userId, email }
 */
export const addUserEmail = async ({ userId, email }) => {
  try {
    return await axios.post(`/users/${userId}/emails/`, { email })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Remove one of a user's additional email addresses.
 * @param {object} payload - { userId, emailId }
 */
export const deleteUserEmail = async ({ userId, emailId }) => {
  try {
    return await axios.delete(`/users/${userId}/emails/${emailId}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Make one of a user's verified addresses their primary one (RSCX-377). The old
 * primary is kept as an additional address, so it still signs in.
 * @param {object} payload - { userId, emailId }
 */
export const promoteUserEmail = async ({ userId, emailId }) => {
  try {
    return await axios.post(`/users/${userId}/emails/${emailId}/promote/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Set which address receives an account's notifications for a user.
 * `email` is a UserEmail id, or null to use the primary address.
 * @param {object} payload - { userId, entityId?, organizationId?, emailId }
 */
export const setPreferredEmail = async ({ userId, entityId, organizationId, emailId }) => {
  try {
    return await axios.post(`/users/${userId}/preferred-email/`, {
      entity: entityId ?? null,
      organization: organizationId ?? null,
      email: emailId ?? null,
    })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Merge a duplicate user account into a primary one (Xverse admin only).
 * @param {object} payload - { primaryId, duplicateId }
 */
export const mergeUsers = async ({ primaryId, duplicateId }) => {
  try {
    return await axios.post(`/users/${primaryId}/merge/`, { duplicate: duplicateId })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Confirm an added email address with its emailed verification code.
 * @param {object} payload - { userId, emailId, code }
 */
export const verifyUserEmail = async ({ userId, emailId, code }) => {
  try {
    return await axios.post(`/users/${userId}/emails/${emailId}/verify/`, { code })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Re-send the verification code for an unverified email address.
 * @param {object} payload - { userId, emailId }
 */
export const resendUserEmailVerification = async ({ userId, emailId }) => {
  try {
    return await axios.post(`/users/${userId}/emails/${emailId}/resend/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Link an emailed invitation to the signed-in account (RSCX-351). The token
 * comes from the invitation's set-password link; the invited shell account is
 * folded into the current one and its address becomes a verified login.
 * @param {string} token
 */
export const claimInvitation = async (token) => {
  try {
    return await axios.post('/claim-invitation/', { token })
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
